import { useNavigate } from "react-router-dom";
import { Plus, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePromotionList } from "./api/promotionHooks";
import { PromotionStatusBadge } from "./components/PromotionStatusBadge";
import { CouponAnalyticsDashboard } from "./components/CouponAnalyticsDashboard";
import { useState } from "react";

export function PromotionsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageLength = 20;
  const { data, isLoading } = usePromotionList(page, pageLength);

  const promotions = data?.promotions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageLength);

  return (
    <div className="mx-auto max-w-5xl px-4 pt-12">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Promotions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Create and manage coupon promotions for your storefront
          </p>
        </div>
        <Button onClick={() => navigate("/promotions/new")}>
          <Plus className="mr-1.5 h-4 w-4" />
          Create Promotion
        </Button>
      </div>

      {/* Analytics Dashboard */}
      <CouponAnalyticsDashboard />

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-sm bg-gray-100" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && promotions.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-sm border border-dashed py-16">
          <Ticket className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <h2 className="text-lg font-medium">No promotions yet</h2>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">
            Create your first promotion to attract customers and boost sales.
          </p>
          <Button onClick={() => navigate("/promotions/new")}>
            <Plus className="mr-1.5 h-4 w-4" />
            Create Promotion
          </Button>
        </div>
      )}

      {/* Table */}
      {!isLoading && promotions.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-sm border sm:block">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Promotion</th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Discount</th>
                  <th className="px-4 py-3 font-medium">Collected</th>
                  <th className="px-4 py-3 font-medium">Used</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {promotions.map((p) => (
                  <tr
                    key={p.coupon_code}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() =>
                      navigate(
                        `/promotions/${encodeURIComponent(p.coupon_code)}`,
                      )
                    }
                  >
                    <td className="px-4 py-3 font-medium">
                      {p.promotion_title}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {p.coupon_code}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          p.is_public
                            ? "bg-blue-50 text-blue-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {p.is_public ? "Public" : "Exclusive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{p.discount_display}</td>
                    <td className="px-4 py-3">
                      {p.collected}
                      {p.max_collections > 0 && (
                        <span className="text-muted-foreground">
                          {" "}
                          / {p.max_collections}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{p.used}</td>
                    <td className="px-4 py-3">
                      <PromotionStatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {promotions.map((p) => (
              <div
                key={p.coupon_code}
                className="cursor-pointer rounded-sm border p-4 hover:bg-gray-50"
                onClick={() =>
                  navigate(
                    `/promotions/${encodeURIComponent(p.coupon_code)}`,
                  )
                }
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{p.promotion_title}</span>
                  <PromotionStatusBadge status={p.status} />
                </div>
                <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="font-mono text-xs">{p.coupon_code}</span>
                  <span>{p.discount_display}</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                  <span>
                    Collected: {p.collected}
                    {p.max_collections > 0 && `/${p.max_collections}`}
                  </span>
                  <span>Used: {p.used}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {total} promotion{total !== 1 ? "s" : ""}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
